import Vue from 'vue';
import VueRouter from 'vue-router';
import EditorPage from '@/components/editor/EditorPage.vue';
import LoginSignUp from '@/components/login/LoginSignUp.vue';
import ProjectPage from '@/components/project/ProjectPage.vue';
import UserModule from '@/store/modules/user';
import { getModule } from 'vuex-module-decorators';
import store from '@/store';
import SceneRoot from '@/components/scenes/SceneRoot.vue';
import Settings from '@/components/account/Settings.vue';
import TutorialPage from '@/components/editor/tutorials/TutorialPage.vue';
import UserApi from '@/apis/user-api';
import UtilHelper, { IUserGTMEvent } from '@/helpers/utils';
import HttpHelper from '@/helpers/http';
import { EUserAdEvent, EUserFlag } from '@/helpers/user';
import VueMeta from 'vue-meta';
import { EModalName } from '@/helpers/modal';
import CheckoutPage from '@/components/checkout/CheckoutPage.vue';
import ConnectPlatform from '@/components/login/ConnectPlatform.vue';
import { ESocketEvent } from '@/helpers/socket';
import { EAnalyticsEvent } from '@/helpers/analytic';

Vue.use(VueRouter);
Vue.use(VueMeta);

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: LoginSignUp,
    meta: {
      title: 'Login',
    },
  },
  {
    path: '/signup',
    name: 'SignUp',
    component: LoginSignUp,
    meta: {
      title: 'Sign Up',
    },
  },
  {
    path: '/verify-email',
    name: 'VerifyEmail',
    component: LoginSignUp,
    meta: {
      title: 'Verify Email',
    },
  },
  {
    path: '/verify-tfa',
    name: 'VerifyTFA',
    component: LoginSignUp,
    meta: {
      title: 'Verify TFA',
    },
  },
  {
    path: '/forgot',
    name: 'Forgot',
    component: LoginSignUp,
    meta: {
      title: 'Forgot',
    },
  },
  {
    path: '/verify-session',
    name: 'Verify Session',
    component: LoginSignUp,
    meta: {
      title: 'Verify Session',
    },
  },
  {
    path: '/connect-platforms',
    name: 'Connect Platforms',
    component: ConnectPlatform,
    meta: {
      auth: true,
      title: 'Connect Platforms',
    },
  },
  {
    path: '/checkout',
    name: 'Checkout',
    component: CheckoutPage,
    meta: {
      auth: true,
      title: 'Checkout',
    },
  },
  {
    path: '/projects',
    name: 'Project',
    component: ProjectPage,
    meta: {
      auth: true,
      title: 'Projects',
      requirePlatform: true,
    },
  },
  {
    path: '/projects/:projectUuid/scenes',
    name: 'Scene',
    component: SceneRoot,
    meta: {
      auth: true,
      title: 'Scenes',
      requirePlatform: true,
    },
  },
  {
    path: '/projects/:projectUuid/scenes/:sceneUuid/editor',
    name: 'Editor',
    component: EditorPage,
    meta: {
      auth: true,
      title: 'Editor',
      requirePlatform: true,
    },
  },
  {
    path: '/account',
    name: 'Account',
    component: Settings,
    meta: {
      auth: true,
      title: 'Account',
    },
  },
  {
    path: '/tutorials',
    name: 'Tutorials',
    component: TutorialPage,
    meta: {
      title: 'Tutorials',
    },
  },
];

const router = new VueRouter({
  mode: 'history',
  routes,
});

router.beforeEach(async (to, from, next) => {
  const userModule = getModule(UserModule, store);

  if (from?.name && userModule.isPreviousPageExternal) {
    userModule.setIsPreviousPageExternal(false);
  }

  if (!userModule.user) {
    try {
      const user = await UserApi.getMe();

      if (user) {
        const userFlag = await UserApi.getUserFlags();
        const userSubscription = await UserApi.getUserSubscription();

        userModule.setUser(user);
        userModule.setUserFlags(userFlag.flags);
        userModule.setSubscription(userSubscription?.subscription);

        const userCsrfToken = await UserApi.getCsrfToken();
        HttpHelper.setCsrfToken(userCsrfToken);

        if (userModule.userFlags.hasOwnProperty(EUserFlag.firstSuccessfulTransaction)) {
          await UserApi.removeFirstSuccessfulTransactionFlag();
        }

        userModule.setSocketServerUrl(user.socketServerUrl);

        if (to.query.intent) {
          const isSignUp = to.query.intent === 'signup';
          const event: IUserGTMEvent = { event: isSignUp ? EUserAdEvent.signUp : EUserAdEvent.logIn };

          if (isSignUp) event.sign_up_source = 'console';

          UtilHelper.trackGTMEvent(event);
        }

        if (to.query.group) {
          userModule.setABGroup((to.query.group) as string);
        }

        if (to.query?.refl === 'article-001') {
          userModule.setABGroup('a');
        }

      } else {
        if (to.meta.auth) {
          return next({ name: 'Login' });
        }
      }
    } catch (e) {
      if (to.meta.auth) {
        return next({ name: 'Login' });
      }
    }
  }

  if (
    userModule.userFlags?.[EUserFlag.showOnboarding] &&
    to.meta.requirePlatform &&
    !userModule.user?.connected_accounts?.length
  ) {
    return next({ name: 'Connect Platforms' });
  }

  if (to.meta.auth && !userModule.user.hasConsentedTerms) Vue.prototype.$modal.show(EModalName.changeOfTerms);

  if (process.env.MIX_ENV !== 'local') {
    userModule.connectEditorSocket().then(() => {
      Vue.prototype.$socket.client.prependAny(async(event, data) => {
        if (Object.values(ESocketEvent).includes(event)) {
          return;
        }

        Vue.prototype.$sla.sendEvent(
          EAnalyticsEvent.unhandleSocketEvent,
          {
            eventName: event,
            data: JSON.stringify(data),
          }
        )
      });
    });
  }

  if (to.meta.title) {
    document.title = `${to.meta.title} | Streamlabs Console`;
  } else {
    document.title = 'Streamlabs Console';
  }

  next();
});

export default router;
